import type { ReactNode } from 'react';

import { useLingui } from '@lingui/react/macro';
import type { AnnotationsProps } from '@repo/image-annotator/lib';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useSelector } from 'react-redux';

import { getReactQueryAxiosInstance } from './api';
import { endpoints } from './endpoints';

interface PushToPMSRequest {
  unifiedAppointmentId: string;
  attachmentID?: string;
  narrative?: string;
  clinicalNote?: string;
  fmxTitle?: string;
  toothLevelImages?: Array<{
    title: string;
    imageId: number;
    annotations?: AnnotationsProps;
    tableData: Array<{
      tooth: string;
      txCodes: string[];
      dmf: string;
      caries: boolean;
      resto: boolean;
    }>;
  }>;
  fmxImages?: Array<{
    imageId: number;
    annotations?: AnnotationsProps;
  }>;
  fmxTableData?: Array<{
    tooth: string;
    txCodes: string[];
    calculus: boolean;
    rblMesial: string | null;
    rblDistal: string | null;
  }>;
}

interface PushToPMSResponse {
  success: boolean;
  message: ReactNode;
}

/**
 * React Query mutation hook for pushing claim attachments to PMS
 *
 * @param options - Optional mutation options
 * @returns React Query mutation object
 */
export const usePushToPMS = (
  options?: Parameters<
    typeof useMutation<PushToPMSResponse, AxiosError, PushToPMSRequest>
  >[0],
) => {
  const { t } = useLingui();
  const user = useSelector(
    (state: {
      login?: {
        user?: { primaryPracticeId?: string; primaryOrganizationId?: string };
      };
    }) => state?.login?.user,
  );
  const currentPracticeId = user?.primaryPracticeId;
  const currentOrganizationId = user?.primaryOrganizationId;

  return useMutation({
    mutationFn: async (
      request: PushToPMSRequest,
    ): Promise<PushToPMSResponse> => {
      if (!currentOrganizationId || !currentPracticeId) {
        throw new Error('Missing organization or practice information');
      }

      const instance = getReactQueryAxiosInstance();

      // Create request body, conditionally including fmxImages only if it exists and has items
      const requestBody = {
        unifiedAppointmentId: request.unifiedAppointmentId,
        attachmentID: request.attachmentID,
        narrative: request.narrative,
        clinicalNote: request.clinicalNote,
        fmxTitle: request.fmxTitle,
        toothLevelImages: request.toothLevelImages,
        ...(request.fmxImages &&
          request.fmxImages.length > 0 && { fmxImages: request.fmxImages }),
        ...(request.fmxTableData &&
          request.fmxTableData.length > 0 && {
            fmxTableData: request.fmxTableData,
          }),
      };

      // Call generate-print-view endpoint to get the practice document ID
      const generateResponse = await instance.request({
        method: 'POST',
        url: `/${endpoints.pdfGenerate}`,
        headers: {
          Practice: currentPracticeId,
          Org: currentOrganizationId,
        },
        data: requestBody,
        responseType: 'blob',
      });

      // Extract practice document ID from response header
      const practiceDocumentId =
        generateResponse.headers['x-practice-document-id'];

      if (!practiceDocumentId) {
        throw new Error('Failed to get practice document ID from response');
      }

      // Call writeback endpoint
      await instance.request({
        method: 'POST',
        url: `/${endpoints.claimWriteback}`,
        headers: {
          Practice: currentPracticeId,
          Org: currentOrganizationId,
        },
        data: {
          practiceDocumentId,
        },
      });

      return {
        success: true,
        message: t`Successfully pushed to PMS`,
      };
    },
    ...options,
  });
};
